import { getCategoryUrl, StandaloneArticleTeaserProps } from '@/components/ArticleTeaser/Default';
import { ArticleTeaser, ArticleTeaserProvider } from '@/components/ArticleTeaser/Default/ArticleTeaser';
import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneOptionalLinkProps } from '@/components/OptionalLink';
import Badge from '@/mabra/components/element/Badge';
import type { LabradorComponent, StandaloneComponent } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import { ArticleBadgeData } from 'lib/labrador/articleBadge';
import { withDebugMetadata } from 'lib/utils';
import mergeProps from 'lib/utils/mergeProps';
import { standaloneComponent } from 'modules/dynamic-components';

const OptionalLink = standaloneComponent<StandaloneOptionalLinkProps>('ElementOptionalLink');

export interface MabraStandaloneArticleTeaserProps extends StandaloneArticleTeaserProps {
  badge?: ArticleBadgeData;
}

export const MabraStandaloneArticleTeaser: StandaloneComponent<MabraStandaloneArticleTeaserProps> = ({
  caption,
  description,
  footer,
  header,
  headline,
  image,
  kicker,
  links,
  badge,
  options,
  ...props
}) => {
  const { $caption, $description, $footer, $group, $header, $headline, $image, ...$base } = options ?? {};
  const { colors, size, variant, theme } = $base ?? {};

  return (
    <ErrorBoundary>
      <ArticleTeaserProvider value={{ colors, size: size ?? variant, variant, theme }}>
        <ArticleTeaser {...$base} {...props}>
          {header && <ArticleTeaser.Header {...$header}>{header}</ArticleTeaser.Header>}
          <OptionalLink
            {...links?.article}
            content={<ArticleTeaser.Image {...mergeProps({ options: $image }, image)} />}
          />
          {badge && <Badge data={badge} variant="teaser" />}
          <ArticleTeaser.Group {...$group}>
            {caption && (
              <OptionalLink
                {...links?.caption}
                content={<ArticleTeaser.Caption {...$caption}>{caption}</ArticleTeaser.Caption>}
              />
            )}
            {headline && (
              <OptionalLink
                {...links?.article}
                content={<ArticleTeaser.Headline {...$headline}>{headline}</ArticleTeaser.Headline>}
              />
            )}
            {description && <ArticleTeaser.Description {...$description}>{description}</ArticleTeaser.Description>}
            {kicker && <ArticleTeaser.Description {...$description}>{kicker}</ArticleTeaser.Description>}
          </ArticleTeaser.Group>
          {footer && <ArticleTeaser.Footer {...$footer}>{footer}</ArticleTeaser.Footer>}
        </ArticleTeaser>
      </ArticleTeaserProvider>
    </ErrorBoundary>
  );
};

const MabraLabradorArticleTeaser: LabradorComponent = ({ type, data, meta, descendants }) => {
  const MabraStandaloneArticleTeaser =
    standaloneComponent<MabraStandaloneArticleTeaserProps>('ContentArticleTeaserDefault');

  const [{ device }] = useAppState();

  const { category, subtitle, kicker, title, publishedUrl, badgeData, displayProminentTagOnTeaser } = data ?? {};

  const labradorImage = descendants?.find((descendant) => descendant.type === 'labradorImage')?.data;

  const { imageUrl } = labradorImage ?? {};

  const { showKicker, isNativeAd, nativeAdLabel, teaserIndex, showSubtitle, showTitle, hasFullWidth, showCategory } =
    meta ?? {};

  const src = imageUrl?.[device];

  const showCategoryOnDevice = showCategory?.[device];
  const showKickerOnDevice = showKicker?.[device];
  const showSubtitleOnDevice = showSubtitle?.[device];
  const showTitleOnDevice = showTitle?.[device];
  const hasFullWidthOnDevice = hasFullWidth?.[device];

  const isPriority = teaserIndex < 4;

  const prominentTag = displayProminentTagOnTeaser ? data.prominentTag : undefined;

  const displayCategory = isNativeAd ? nativeAdLabel || category : category;

  return (
    <MabraStandaloneArticleTeaser
      caption={showCategoryOnDevice && (prominentTag || displayCategory)}
      description={showSubtitleOnDevice && subtitle}
      kicker={showKickerOnDevice && kicker}
      headline={showTitleOnDevice && title}
      links={{
        article: { href: publishedUrl },
        caption: { href: getCategoryUrl(data, meta) },
      }}
      image={{
        src,
        options: { priority: isPriority },
      }}
      badge={badgeData}
      data-full-width={hasFullWidthOnDevice ? '' : undefined}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export const standalone = MabraStandaloneArticleTeaser;

export default MabraLabradorArticleTeaser;
